import sharedStateBridge, {
  ComposeStore,
  composeTabs,
  IGUserTag,
  MediaFileType,
  MediaIdType,
  MediaType,
  MessageMediaType,
  PostType,
  TabsState,
  TabState,
  ValidationDetailType,
} from '@sendible/shared-state-bridge';
import { publish, EVENTS } from '@sendible/event-manager';
import { COMPOSE_TABS, defaultCompose } from './consts';
import { ALT_TEXT_SUPPORTED_TABS } from '../../constants/feature-support';
import { tabToNetworkMapping } from '../../models/medias/consts';

const clearComposeBoxState = () => {
  publish(EVENTS.CANCEL_ALL_UPLOADS_AND_CLOSE_BU, {});

  const sharedState = sharedStateBridge.getSharedState();

  sharedStateBridge.setItem({
    ...sharedState,
    compose: { ...defaultCompose },
  });
};

const getAttachedMedia = (tabId: composeTabs | undefined): MediaAttachedType | undefined => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { activeComposeBoxTab, tabsState, attachedTwMedia } = composeState;

  const mediaAttached: MediaAttachedType = {};
  const activeTab = tabsState[tabId || activeComposeBoxTab];

  if (activeTab?.messageMedias && attachedTwMedia) {
    Object.keys(activeTab.messageMedias).forEach((messageMediaId) => {
      const mediaId = Number(messageMediaId) as MediaIdType;
      const messageMedia = activeTab.messageMedias?.[mediaId];
      const twMedia: MediaType = attachedTwMedia[mediaId];

      if (messageMedia && twMedia) {
        const combined: MediaType & MessageMediaType = {
          ...messageMedia,
          ...twMedia,
        };

        mediaAttached[mediaId] = {
          ...combined,
        };
      }
    });
  }

  return mediaAttached;
};
const getTabsState = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  return composeState?.tabsState;
};

const getCurrentPostType = (tabId: composeTabs) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { activeComposeBoxTab, tabsState } = composeState;

  return tabsState && tabsState[tabId || activeComposeBoxTab]?.postType;
};

const hasRequiredValidationForNetwork = (media: MediaType, networkName: string, postType: PostType) => {
  const hasRequiredValidation =
    media.validations &&
    media.validations[networkName] &&
    media.validations[networkName][postType]?.some((validation: ValidationDetailType) => validation.level === 'Required');

  const hasRequiredPosterValidation =
    media.components?.poster?.validations &&
    media.components.poster.validations[networkName] &&
    media.components.poster.validations[networkName][postType]?.some((validation: ValidationDetailType) => validation.level === 'Required');

  return hasRequiredValidation || hasRequiredPosterValidation;
};

const getShouldShowGenericError = (tabId: composeTabs) => {
  const attachedMediasOnActiveTab = Object.values(getAttachedMedia(tabId) || {});
  const postType = getCurrentPostType(tabId);

  if (!attachedMediasOnActiveTab || !postType) return undefined;

  if (tabId === COMPOSE_TABS.GROUP) {
    const tabsState = getTabsState();
    const profileListServices: composeTabs[] = tabsState?.[COMPOSE_TABS.GROUP]?.profileListServices || [];

    return attachedMediasOnActiveTab.some((media: MediaType) =>
      profileListServices.some((service) => {
        const networkName = tabToNetworkMapping[service];

        return networkName && hasRequiredValidationForNetwork(media, networkName, postType);
      })
    );
  }

  const networkName = tabToNetworkMapping[tabId];

  if (!networkName) return undefined;

  return attachedMediasOnActiveTab.some((media: MediaType) => hasRequiredValidationForNetwork(media, networkName, postType));
};

const setActiveComposeBoxTab = (tabId: composeTabs | undefined) => sharedStateBridge.updateSpecificKey('compose', 'activeComposeBoxTab', tabId);

const setIsComposeBoxOpen = (state: boolean) => sharedStateBridge.updateSpecificKey('compose', 'isComposeBoxOpen', state);

const setIsMediaAttacherOpen = (state: boolean) => sharedStateBridge.updateSpecificKey('compose', 'isMediaAttacherOpen', state);

const toggleMediaAttacher = () => {
  const currentMediaAttacherState = sharedStateBridge.getSharedState().compose.isMediaAttacherOpen || false;

  sharedStateBridge.updateSpecificKey('compose', 'isMediaAttacherOpen', !currentMediaAttacherState);
};

const processNewTabState = (tabId: composeTabs, tabState: TabState) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const supportsAltText = ALT_TEXT_SUPPORTED_TABS.includes(tabId);
  const originalTabMedias = composeState.tabsState[COMPOSE_TABS.MESSAGE]?.messageMedias;

  return {
    ...tabState,
    postType: tabState?.postType || 'post',
    messageMedias: Object.fromEntries(
      Object.entries(originalTabMedias || {}).map(([id, media]) => {
        if (supportsAltText) {
          return [id, { ...media }];
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { altText, ...mediaWithoutAltText } = media;

        return [id, mediaWithoutAltText];
      })
    ),
  };
};

const updateNonCustomisedTabs = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState && composeState.tabsState[COMPOSE_TABS.MESSAGE]?.messageMedias) {
    const newTabsState = { ...composeState.tabsState };

    (Object.entries(composeState.tabsState) as [composeTabs, TabState][])
      .filter(([tabId, tabState]) => tabId !== COMPOSE_TABS.MESSAGE && tabState && !tabState.isCustomised)
      .forEach(([tabId, tabState]) => {
        if (tabState) {
          newTabsState[tabId] = processNewTabState(tabId, tabState);
        }
      });
    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  }
};

const updateIGTagging = (mediaId: MediaIdType, tabId: composeTabs, igUserTags: IGUserTag[]) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const currentMedia = tabsState[tabId]?.messageMedias?.[mediaId];

  if (currentMedia) {
    const updatedMedia = {
      ...currentMedia,
      igUserTags,
    };
    const newTabsState = {
      ...tabsState,
      [tabId]: { ...tabsState[tabId], messageMedias: { ...tabsState[tabId]?.messageMedias, [mediaId]: updatedMedia } },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
    publish(EVENTS.MEDIA_PROPERTIES_UPDATED, { mediaId, tabId });
    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
  }
};

const updateFileDisplayName = (mediaId: MediaIdType, tabId: composeTabs, fileDisplayName: string) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const currentMedia = tabsState[tabId]?.messageMedias?.[mediaId];

  if (currentMedia) {
    const updatedMedia = {
      ...currentMedia,
      fileDisplayName,
    };
    const newTabsState = {
      ...tabsState,
      [tabId]: { ...tabsState[tabId], messageMedias: { ...tabsState[tabId]?.messageMedias, [mediaId]: updatedMedia } },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
    publish(EVENTS.MEDIA_PROPERTIES_UPDATED, { mediaId, tabId });
    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
  }
};

const updateAltText = (mediaId: MediaIdType, tabId: composeTabs, altText: string) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const currentMedia = tabsState[tabId]?.messageMedias?.[mediaId];

  if (currentMedia) {
    const updatedMedia = {
      ...currentMedia,
      altText,
    };
    const newTabsState = {
      ...tabsState,
      [tabId]: { ...tabsState[tabId], messageMedias: { ...tabsState[tabId]?.messageMedias, [mediaId]: updatedMedia } },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
    publish(EVENTS.MEDIA_PROPERTIES_UPDATED, { mediaId, tabId });
    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
  }
};

const setImageEmbedded = (mediaId: MediaIdType) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const currentMedia = tabsState[COMPOSE_TABS.MESSAGE]?.messageMedias?.[mediaId];

  if (currentMedia) {
    const updatedMedia = {
      ...currentMedia,
      isEmbeddedImage: true,
    };

    const newTabsState = {
      ...tabsState,
      [COMPOSE_TABS.MESSAGE]: {
        ...tabsState[COMPOSE_TABS.MESSAGE],
        messageMedias: { ...tabsState[COMPOSE_TABS.MESSAGE]?.messageMedias, [mediaId]: updatedMedia },
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  }
};

const updateVideoSettings = (mediaId: MediaIdType, tabId: composeTabs, videoSettings: VideoSettings) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const currentMedia = tabsState[tabId]?.messageMedias?.[mediaId];

  if (currentMedia) {
    const updatedMedia = {
      ...currentMedia,
      videoSettings,
    };
    const newTabsState = {
      ...tabsState,
      [tabId]: { ...tabsState[tabId], messageMedias: { ...tabsState[tabId]?.messageMedias, [mediaId]: updatedMedia } },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
    publish(EVENTS.MEDIA_PROPERTIES_UPDATED, { mediaId, tabId });

    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
  }
};
const attachMedia = (
  { id, type, altText, igUserTags = [], videoSettings, orderNo, fileDisplayName }: Partial<MessageMediaType>,
  tabId: composeTabs
) => {
  if (!id || !type) {
    return;
  }
  setIsMediaAttacherOpen(false);

  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, attachedTwMedia } = composeState;

    const currentMessageMedias = tabsState[tabId]?.messageMedias || {};
    const maxOrderNo = Object.values(currentMessageMedias).reduce((max: number, media: MessageMediaType) => Math.max(max, media?.orderNo ?? 0), 0);

    const newTabsState = {
      ...tabsState,
      [tabId]: {
        ...tabsState[tabId],
        isCustomised: true,
        messageMedias: {
          ...currentMessageMedias,
          [id]: {
            id,
            type,
            altText,
            igUserTags,
            videoSettings,
            fileDisplayName,
            orderNo: orderNo ?? maxOrderNo + 1,
          },
        },
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

    const newAttachedTwMedia = {
      ...attachedTwMedia,
      [id]: attachedTwMedia?.[id] || {},
    };

    sharedStateBridge.updateSpecificKey('compose', 'attachedTwMedia', newAttachedTwMedia);

    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }

    publish(EVENTS.MEDIA_ADDED, tabId);
  }
};

const deleteOrphanTwMedia = (mediaIdToRemove: MediaIdType) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, attachedTwMedia } = composeState;
    const mediaStillUsed = Object.values(tabsState).some((tab) => tab?.messageMedias && tab.messageMedias[mediaIdToRemove]);

    if (!mediaStillUsed && attachedTwMedia?.[mediaIdToRemove]) {
      const newAttachedTwMedia = { ...attachedTwMedia };

      delete newAttachedTwMedia[mediaIdToRemove];
      sharedStateBridge.updateSpecificKey('compose', 'attachedTwMedia', newAttachedTwMedia);
    }
  }
};

const replaceMedia = (originalMediaId: MediaIdType, newMediaId: MediaIdType, tabId: composeTabs) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, attachedTwMedia } = composeState;

    const currentMediaAttached = tabsState[tabId]?.messageMedias || {};
    const originalMedia = currentMediaAttached[originalMediaId];
    const { orderNo, type } = originalMedia;
    const newMessageMedias: TabState['messageMedias'] = {};

    Object.entries(currentMediaAttached).forEach(([id, messageMedia]) => {
      const numericId = Number(id);

      if (numericId === originalMediaId) {
        newMessageMedias[newMediaId] = {
          ...messageMedia,
          id: newMediaId,
          orderNo,
          type,
        };
      } else {
        newMessageMedias[numericId] = messageMedia;
      }
    });

    const newTabsState = {
      ...tabsState,
      [tabId]: {
        ...tabsState[tabId],
        isCustomised: true,
        messageMedias: newMessageMedias,
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

    const newAttachedTwMedia = {
      ...attachedTwMedia,
      [newMediaId]: attachedTwMedia?.[newMediaId] || {},
    };

    sharedStateBridge.updateSpecificKey('compose', 'attachedTwMedia', newAttachedTwMedia);

    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }

    publish(EVENTS.MEDIA_REMOVED, { mediaId: originalMediaId, tabId });
    publish(EVENTS.MEDIA_ADDED, tabId);
    deleteOrphanTwMedia(originalMediaId);
  }
};

const getIsUploadingMedias = (tabId: composeTabs) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState, attachedTwMedia } = composeState;

  if (attachedTwMedia && tabsState[tabId]?.messageMedias) {
    const isUploading = !!Object.values(tabsState[tabId]?.messageMedias || {}).some((media) => attachedTwMedia[media.id]?.status !== 'Ready');

    return isUploading;
  }
};

const getIsUploadingMediasOnAnyTab = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { attachedTwMedia } = composeState;

  return Object.values(attachedTwMedia || {}).some((media) => media.status !== 'Ready');
};
const getAttachedMediaCount = (tabId?: composeTabs) => {
  return Object.keys(getAttachedMedia(tabId) || {}).length;
};
const convertMediaAttachedToSortedArray = (mediaAttached: MediaAttachedType | undefined) => {
  if (!mediaAttached) {
    return [];
  }

  return Object.values(mediaAttached).sort((a, b) => a.orderNo - b.orderNo);
};

const updateMediaProperties = (media: MediaType) => {
  const attachedTwMedia = sharedStateBridge.getSharedState()?.compose?.attachedTwMedia;

  const updatedMediaAttached = {
    ...attachedTwMedia,
    [media.id]: media,
  };

  sharedStateBridge.updateSpecificKey('compose', 'attachedTwMedia', updatedMediaAttached);

  publish(EVENTS.MEDIA_UPDATED, { id: media.id, type: media.type, url: media.originalUrl, mimeType: media.metadata.contentType });
};

const getMediaDataForSaving = (tabId: composeTabs) => {
  const compose: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState, attachedTwMedia } = compose;
  const mediaData: NewMedia[] = [];
  const embeddedMediaData: NewMedia[] = [];

  if (!attachedTwMedia || !tabsState[tabId]?.messageMedias) {
    return { mediaData, embeddedMediaData };
  }

  // NOTE: Convert to array and sort by orderNo to maintain order
  const mediaAttached = convertMediaAttachedToSortedArray(getAttachedMedia(tabId));

  mediaAttached.forEach((media) => {
    const defaultData: NewMedia = {
      twMedia: {
        id: media.id,
        type: media?.metadata?.contentType || '',
      },
      // NOTE: videos do not have file names, so falling back to
      // 'Attachment' as BE requires this field.
      name: media.name || 'Attachment',
      url: media.originalUrl,
      altText: media?.altText,
      igUserTags: media?.igUserTags,
    };

    if (media.type === 'Video') {
      defaultData.videoFileSize = media.metadata.size;
      defaultData.videoProperties = {
        title: media?.videoSettings?.title || ' ',
        youtube: {
          tags: media?.videoSettings?.membersToNotify.join(',') || '',
          category: media?.videoSettings?.category || ' ',
          privacyStatus: media?.videoSettings?.youtubePrivacyStatus || 'public',
        },
        thumbnail: {
          url: media?.posterUrl || media.thumbnailUrl,
          offset:
            media?.videoSettings?.frameInMiliseconds && Number.isFinite(media?.videoSettings?.frameInMiliseconds)
              ? media?.videoSettings?.frameInMiliseconds
              : 0,
        },
      };
    }

    if (media.type === MediaFileType.Document && media.fileDisplayName && media.fileDisplayName !== media.name) {
      defaultData.fileDisplayName = media.fileDisplayName;
    }

    if (media.isEmbeddedImage) {
      embeddedMediaData.push(defaultData);
    } else {
      mediaData.push(defaultData);
    }
  });

  return { mediaData, embeddedMediaData };
};

const setTabsState = (tabInfo: Partial<TabsState>) => {
  const tabsState = sharedStateBridge.getSharedState()?.compose?.tabsState;

  const newTabsState: TabsState = {};

  Object.entries(tabInfo).forEach(([tabId, tabInfoState]) => {
    // for each tab present in compose box
    newTabsState[tabId as composeTabs] = {
      ...tabsState[tabId as composeTabs], // whatever media we had previously attached
      ...tabInfoState, // whatever state comes from compose box
    };
  });
  sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  updateNonCustomisedTabs();
};

const reorderMedia = (oldIndex: number, newIndex: number, tabId: composeTabs) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  const messageMediasReordered: TabState['messageMedias'] = {};

  if (!tabsState[tabId]?.messageMedias) {
    return;
  }

  const currentMessageMedias = Object.values(tabsState[tabId]?.messageMedias || {}).sort((a, b) => a.orderNo - b.orderNo);
  const movedItem = currentMessageMedias[oldIndex];

  // Remove the item from the old position
  currentMessageMedias.splice(oldIndex, 1);
  // Insert the item at the new position
  currentMessageMedias.splice(newIndex, 0, movedItem);

  // Reassign orderNo based on new position
  currentMessageMedias.forEach((media, index) => {
    messageMediasReordered[media.id] = {
      ...media,
      orderNo: index + 1,
    };
  });

  const newTabsState = {
    ...tabsState,
    [tabId]: {
      ...tabsState[tabId],
      isCustomised: true,
      messageMedias: messageMediasReordered,
    },
  };

  sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  publish(EVENTS.MEDIA_PROPERTIES_UPDATED, { mediaId: movedItem.id, tabId });

  if (tabId === COMPOSE_TABS.MESSAGE) {
    updateNonCustomisedTabs();
  }
};

const removeMedia = (mediaIdToRemove: MediaIdType, tabId: composeTabs) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState } = composeState;

    const currentMessageMedias = tabsState[tabId]?.messageMedias || {};
    const mediaToRemove = currentMessageMedias[mediaIdToRemove];
    const removedOrderNo = mediaToRemove.orderNo;
    const updatedMessageMedias = { ...currentMessageMedias };

    delete updatedMessageMedias[mediaIdToRemove];

    // NOTE: Resequence orderNo for items that came after the removed item
    Object.values(updatedMessageMedias).forEach((media) => {
      if (media?.orderNo && media.orderNo > removedOrderNo) {
        media.orderNo -= 1;
      }
    });

    const newTabsState: TabsState = {
      ...tabsState,
      [tabId]: {
        ...tabsState[tabId],
        isCustomised: true,
        postType: tabsState[tabId]?.postType || 'post',
        messageMedias: updatedMessageMedias,
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

    if (tabId === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }

    publish(EVENTS.MEDIA_REMOVED, { mediaId: mediaIdToRemove, tabId });
    deleteOrphanTwMedia(mediaIdToRemove);
  }
};

const getIsPhotoLinkPreview = (photo: { tw_media?: { id: MediaIdType }; is_link_preview: boolean; file_file_size: number | null }) =>
  photo.is_link_preview && !photo.file_file_size;

const setIsNewMediaSystem = (photos?: { tw_media?: { id: MediaIdType }; is_link_preview: boolean; file_file_size: number | null }[]) => {
  const photosWithoutLinkPreviews = photos?.filter((photo) => !getIsPhotoLinkPreview(photo));
  const isMediaSystemFeatureTagEnabled = window.js_featureTagsManager?.isFeatureTag('mediaSystem');

  if (isMediaSystemFeatureTagEnabled && (!photosWithoutLinkPreviews?.length || photosWithoutLinkPreviews?.some((photo) => photo.tw_media))) {
    sharedStateBridge.updateSpecificKey('compose', 'isNewMediaSystem', true);
  } else {
    sharedStateBridge.updateSpecificKey('compose', 'isNewMediaSystem', false);
  }
};

const getIsNewMediaSystem = () => {
  return sharedStateBridge.getSharedState()?.compose?.isNewMediaSystem;
};

const removeTwMedia = (mediaIdToRemove: MediaIdType) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, attachedTwMedia } = composeState;

    if (!attachedTwMedia || !attachedTwMedia[mediaIdToRemove]) {
      return;
    }

    const newAttachedTwMedia = { ...attachedTwMedia };

    delete newAttachedTwMedia[mediaIdToRemove];

    sharedStateBridge.updateSpecificKey('compose', 'attachedTwMedia', newAttachedTwMedia);

    // Remove from all tabs
    const newTabsState = { ...tabsState };

    Object.entries(tabsState).forEach(([tabId, tabState]) => {
      if (tabState?.messageMedias && tabState.messageMedias[mediaIdToRemove]) {
        const newMessageMedias = { ...tabState.messageMedias };

        delete newMessageMedias[mediaIdToRemove];

        newTabsState[tabId as composeTabs] = {
          ...tabState,
          messageMedias: newMessageMedias,
        };
        publish(EVENTS.MEDIA_REMOVED, { mediaId: mediaIdToRemove, tabId });
      }
    });

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  }
};

/* 🛠️ Fixed: Explicitly declare the public API interface signature type.
   By avoiding broad internal type inference, we stop TypeScript from searching
   for unexported deeply-nested types inside the bridge library dependency. */
export const composeMediaInteractions: {
  attachMedia: (
    media: { id: MediaIdType; type: MediaFileType; altText?: string; igUserTags?: IGUserTag[]; videoSettings?: VideoSettings; orderNo?: number },
    tabId: composeTabs
  ) => void;
  clearComposeBoxState: () => void;
  convertMediaAttachedToSortedArray: (mediaAttached: MediaAttachedType | undefined) => (MediaType & MessageMediaType)[];
  getAttachedMedia: (tabId: composeTabs | undefined) => MediaAttachedType | undefined;
  getAttachedMediaCount: (tabId?: composeTabs) => number;
  getMediaDataForSaving: (tabId: composeTabs) => { mediaData: NewMedia[]; embeddedMediaData: NewMedia[] };
  getIsPhotoLinkPreview: (photo: { tw_media?: { id: MediaIdType }; is_link_preview: boolean; file_file_size: number | null }) => boolean;
  getIsUploadingMedias: (tabId: composeTabs) => boolean | undefined;
  getIsUploadingMediasOnAnyTab: () => boolean;
  getShouldShowGenericError: (tabId: composeTabs) => boolean | undefined;
  removeMedia: (mediaIdToRemove: MediaIdType, tabId: composeTabs) => void;
  reorderMedia: (oldIndex: number, newIndex: number, tabId: composeTabs) => void;
  replaceMedia: (originalMediaId: MediaIdType, newMediaId: MediaIdType, tabId: composeTabs) => void;
  deleteOrphanTwMedia: (mediaIdToRemove: MediaIdType) => void;
  setActiveComposeBoxTab: (tabId: composeTabs | undefined) => void;
  setIsComposeBoxOpen: (state: boolean) => void;
  setIsMediaAttacherOpen: (state: boolean) => void;
  setTabsState: (tabInfo: Partial<TabsState>) => void;
  setImageEmbedded: (mediaId: MediaIdType) => void;
  toggleMediaAttacher: () => void;
  updateMediaProperties: (media: MediaType) => void;
  setIsNewMediaSystem: (photos?: { tw_media?: { id: MediaIdType }; is_link_preview: boolean; file_file_size: number | null }[]) => void;
  getIsNewMediaSystem: () => boolean | undefined;
  removeTwMedia: (mediaIdToRemove: MediaIdType) => void;
  updateAltText: (mediaId: MediaIdType, tabId: composeTabs, altText: string) => void;
  updateFileDisplayName: (mediaId: MediaIdType, tabId: composeTabs, fileDisplayName: string) => void;
  updateIGTagging: (mediaId: MediaIdType, tabId: composeTabs, igUserTags: IGUserTag[]) => void;
  updateVideoSettings: (mediaId: MediaIdType, tabId: composeTabs, videoSettings: VideoSettings) => void;
  getTabsState: () => TabsState | undefined;
} = {
  attachMedia,
  clearComposeBoxState,
  convertMediaAttachedToSortedArray,
  getAttachedMedia,
  getAttachedMediaCount,
  getMediaDataForSaving,
  getIsPhotoLinkPreview,
  getIsUploadingMedias,
  getIsUploadingMediasOnAnyTab,
  getShouldShowGenericError,
  removeMedia,
  reorderMedia,
  replaceMedia,
  deleteOrphanTwMedia,
  setActiveComposeBoxTab,
  setIsComposeBoxOpen,
  setIsMediaAttacherOpen,
  setTabsState,
  setImageEmbedded,
  toggleMediaAttacher,
  updateMediaProperties,
  setIsNewMediaSystem,
  getIsNewMediaSystem,
  removeTwMedia,
  updateAltText,
  updateFileDisplayName,
  updateIGTagging,
  updateVideoSettings,
  getTabsState,
};
